import { apiRequest } from '@/utils/api-request';
import { useOrgStore } from '@/stores/org-store';
import { DatasetCsvUploadMetadata } from '@/types/source-types';

interface UploadMetadataSuccess {
  metadata: DatasetCsvUploadMetadata | null;
}

interface UploadMetadataError {
  detail: string;
}

type UploadMetadataResult = UploadMetadataSuccess | UploadMetadataError;

const extractMetadata = (data: unknown): DatasetCsvUploadMetadata | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  if ('metadata' in data) {
    const nested = (data as { metadata?: unknown }).metadata;
    return extractMetadata(nested);
  }

  return data as DatasetCsvUploadMetadata;
};

export const getUploadMetadata = async (
  collectionId: string,
  fileKey: string,
): Promise<UploadMetadataResult> => {
  const orgId = useOrgStore.getState().currentOrg?.orgid;

  if (!orgId) {
    throw new Error('Organization ID is required');
  }

  if (!collectionId) {
    throw new Error('Collection ID is required');
  }

  if (!fileKey) {
    throw new Error('Filename is required');
  }

  const baseFilename = fileKey.split('/').pop();

  if (!baseFilename) {
    throw new Error('Filename is required');
  }

  const response = await apiRequest<
    | DatasetCsvUploadMetadata
    | {
        metadata?: DatasetCsvUploadMetadata;
      }
  >({
    url: `/uploads/${orgId}/${collectionId}/${encodeURIComponent(
      baseFilename,
    )}/metadata`,
    method: 'GET',
    target: 'platform',
  });

  if (response?.detail) {
    return { detail: response.detail };
  }

  const metadata = extractMetadata(response?.data ?? null);
  return { metadata };
};
